import EventEmitter from "eventemitter3";
import { updateScope } from "PFApp/components/filters_v2/stores/filters_v2_store/mutators/check_scope_limit";
import { FiltersMetaV2 } from "PFTypes/filters_v2";
import { PageTarget } from "PFTypes/saved_filters";
import { JSX, PropsWithChildren, useState } from "react";
import { useLocation } from "react-router";
import { RequireAtLeastOne } from "type-fest";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { getTogglePropertyAction } from "./actions/get_toggle_property_action";
import { AutoSaveFiltersEffect } from "./effects/auto_save_filters_effect";
import { emitFiltersMetaSynced, emitFiltersUpdate } from "./events";
import { FiltersV2EventHandlers } from "./filters_v2_events";
import { FiltersV2StorePage, FiltersV2StoreType } from "./filters_v2_store.types";
import { FiltersV2StoreContext, getInitialFiltersV2Data, getPersistConfig } from "./filters_v2_store_context";
import * as mutators from "./mutators";
import { validateAndEmitFiltersUpdate } from "./mutators";

export type FiltersV2StoreContextProviderProps = PropsWithChildren<
  {
    persistent?: boolean;
  } & RequireAtLeastOne<{ name: FiltersV2StorePage; collection: PageTarget }>
>;

export const FiltersV2StoreContextProvider = ({
  children,
  name,
  collection,
  persistent
}: FiltersV2StoreContextProviderProps): JSX.Element => {
  const { pathname } = useLocation();

  const storeId = name ?? pathname;
  const [store] = useState(
    createStore<FiltersV2StoreType>()(
      persist(
        immer((set) => ({
          ...getInitialFiltersV2Data(),
          id: storeId,
          persistent: persistent ?? !!collection,
          collection,
          eventsBus: new EventEmitter<FiltersV2EventHandlers>(),
          actions: {
            updateFilter: (filterDataToUpdate) =>
              set((draftState) => {
                mutators.updateFilter(draftState.buffer, filterDataToUpdate);

                const updatedFilter = mutators.updateFilter(draftState, filterDataToUpdate);
                validateAndEmitFiltersUpdate(draftState, [updatedFilter]);
              }),
            updateFilters: (filtersDataToUpdate) =>
              set((draftState) => {
                mutators.updateFilters(draftState.buffer, filtersDataToUpdate);
                const updatedFilters = mutators.updateFilters(draftState, filtersDataToUpdate);

                validateAndEmitFiltersUpdate(draftState, updatedFilters);

                emitFiltersUpdate(draftState, updatedFilters);
              }),
            updateBufferedFilter: (filterDataToUpdate) =>
              set((draftState) => {
                mutators.updateFilter(draftState.buffer, filterDataToUpdate);
                mutators.autoRefreshFilters(draftState);
              }),
            restoreBufferedFilters: () =>
              set((draftState) => {
                draftState.buffer.mainFilters = draftState.mainFilters;
                draftState.buffer.childrenFilters = draftState.childrenFilters;
              }),
            applyBufferedFilters: () =>
              set((draftState) => {
                const updatedFilters = mutators.applyBuffer(draftState);
                validateAndEmitFiltersUpdate(draftState, updatedFilters);
              }),
            setFilters: ({ mainFilters, childrenFilters }: FiltersMetaV2) =>
              set((draftState) => {
                mutators.resetMainFilters(draftState, mainFilters);
                mutators.resetChildrenFilters(draftState, childrenFilters || {});
                emitFiltersMetaSynced(draftState);
              }),
            resetFilters: (initialFilters) =>
              set((draftState) => {
                mutators.resetAll(draftState, initialFilters);
              }),
            clearValues: (options = {}) =>
              set((draftState) => {
                mutators.clearFilterValues(draftState.buffer, options);
                const updatedFilters = mutators.clearFilterValues(draftState, options);
                emitFiltersUpdate(draftState, updatedFilters);
              }),
            clearBufferValues: (options = {}) =>
              set((draftState) => {
                mutators.clearFilterValues(draftState.buffer, options);
                mutators.autoRefreshFilters(draftState);
              }),
            resetValues: (options = {}) =>
              set((draftState) => {
                mutators.resetFilterValues(draftState.buffer, options);
                const updatedFilters = mutators.resetFilterValues(draftState, options);
                emitFiltersUpdate(draftState, updatedFilters);
              }),
            resetBufferValues: (options = {}) =>
              set((draftState) => {
                mutators.resetFilterValues(draftState.buffer, options);
                mutators.autoRefreshFilters(draftState);
              }),
            toggleChildrenFiltersInBuffer: (on) =>
              set((draftState) => {
                const wasChildrenToggleOn = draftState.buffer.areChildrenOn;
                draftState.buffer.areChildrenOn = on === undefined ? !wasChildrenToggleOn : on;

                if (!wasChildrenToggleOn && draftState.buffer.areChildrenOn) {
                  mutators.resetValues(draftState.buffer.childrenFilters);
                }
                mutators.autoRefreshFilters(draftState);
              }),
            toggleAutoRefresh: (on) => {
              set((draftState) => {
                const wasAutoRefreshOn = draftState.isAutoRefreshOn;
                draftState.isAutoRefreshOn = on === undefined ? !draftState.isAutoRefreshOn : on;

                if (!wasAutoRefreshOn) {
                  mutators.autoRefreshFilters(draftState);
                }
              });
            },
            toggleFiltersSidePanel: (on) =>
              set((draftState) => {
                draftState.isSidePanelOpen = on === undefined ? !draftState.isSidePanelOpen : on;
                if (!draftState.isSidePanelOpen) {
                  mutators.resetBuffer(draftState);
                }
              }),
            toggleAreLoading: getTogglePropertyAction(set, "areLoading"),
            toggleChildrenFilters: getTogglePropertyAction(set, "areChildrenOn"),
            setSearchId: (id) =>
              set((draftState) => {
                draftState.searchId = id;
              }),
            setScope: (entriesAmount) =>
              set((draftState) => {
                updateScope(draftState, entriesAmount);
              }),
            setFiltersCollection: (collection) =>
              set((draftState) => {
                if (draftState.collection !== collection) {
                  mutators.resetAll(draftState);
                }
                draftState.collection = collection;
              })
          }
        })),
        getPersistConfig(storeId)
      )
    )
  );

  return (
    <FiltersV2StoreContext.Provider value={store}>
      <AutoSaveFiltersEffect />
      {children}
    </FiltersV2StoreContext.Provider>
  );
};
